import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Stack, styled, Typography } from '@mui/material';
import { StyledParams } from 'types/common';
import { HeaderPage } from 'app/components/elements/HeaderPage';
import { HeaderFile } from 'app/pages/FileDetail/components/HeaderFileDetail';
import { LeadBoxMemo } from 'app/pages/FileDetail/components/LeadBox';
import { ButtonCustom } from 'app/components/elements/ButtonCustom';
import { useInjection } from 'app/pages/FileDetail/hook';
import { ModalNewLead } from 'app/components/modules/ModalNewLead';
import { BoxPhotoMemo } from 'app/pages/FileDetail/components/BoxPhoto';
import { BoxFileDocumentMemo } from 'app/pages/FileDetail/components/BoxFileDocument';
import { SummaryLog } from './components/SummaryLog';
import { includes } from 'lodash';
import { ModalReport } from './components/ModalReport';
import { ListBoxMemo } from './components/ListInbox';
import { FormReminder } from './components/FormReminder';
import dayjs from 'dayjs';
import { ROLE } from 'const/common';
export const leadTypeOrder = [
  'POR',
  'MVS',
  'POE',
  'PHONE_NUMBER',
  'REFERENCE',
  'VIN',
  'CARFAX',
  'OTHER_ADDRESS',
  'OTHER_PHONE_NUMBER',
  'OTHER_OPEN_NOTE',
  'TOW_YARDS'
];
function FileDetailMobile() {
  const {
    leads,
    modalNewLead,
    setModalNewLead,
    handleCreateLead,
    handleDeleteNoteLead,
    file,
    handleAddNotePhotos,
    handleAddNewNote,
    handleEditNote,
    handleChooseRuleOut,
    handleChooseNotRegister,
    handleRemindLead,
    handleChooseEarlyMorning,
    handleChooseDoorKnock,
    onClickSeized,
    handleAddTowYards,
    handleDeleteTowYards,
    handleChangeVinStatus,
    handleUploadFileCarfax,
    handleDeleteDocumentLead,
    handleUpdateBailiff,
    idsLeadOpen,
    setIdsLeadOpen,
    showQuickNote,
    setShowQuickNote,
    textQuickNote,
    setTextQuickNote,
    handleAddQuickNote,
    showModalReport,
    setShowModalReport,
    asyncHandleCreateReport,
    openModalReminder,
    asyncHandleRequestMvs,
    handleCreateReminder,
    setOpenModalReminder
  } = useInjection();

  const sortedLeads = leads?.slice().sort((a, b) => {
    const indexA = leadTypeOrder.indexOf(a.type ?? '');
    const indexB = leadTypeOrder.indexOf(b.type ?? '');
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  return (
    <Stack width={'100%'}>
      <Helmet>
        <title>Surrey Bailiffs - File Details</title>
      </Helmet>
      <Stack width={'100%'} maxWidth={'1080px'}>
        <HeaderPage title={'File Details'} isBack />
        <Stack style={{ padding: '20px 16px 0 16px' }}>
          <HeaderFile
            textQuickNote={textQuickNote}
            setTextQuickNote={setTextQuickNote}
            file={file}
            showQuickNote={showQuickNote}
            setShowQuickNote={setShowQuickNote}
            handleAddQuickNote={handleAddQuickNote}
          />
        </Stack>
        <WrapperLead>
          <TitleLead style={{ paddingLeft: '11px' }}>Lead Details</TitleLead>
          <ListLead>
            {sortedLeads?.map((item, index) => (
              <LeadBoxMemo
                onOpenLead={() => {
                  const hasId = includes(idsLeadOpen, Number(item.id));
                  if (hasId) {
                    setIdsLeadOpen((prevState) =>
                      prevState.filter((pre) => pre !== Number(item.id))
                    );
                    return;
                  }
                  setIdsLeadOpen((prevState) => [...prevState, Number(item.id)]);
                }}
                isOpen={includes(idsLeadOpen, Number(item.id))}
                handleChooseEarlyMorning={handleChooseEarlyMorning}
                handleChooseDoorKnock={handleChooseDoorKnock}
                handleChooseRuleOut={handleChooseRuleOut}
                handleChooseNotRegister={handleChooseNotRegister}
                handleRemindLead={handleRemindLead}
                handleAddNewNote={handleAddNewNote}
                handleEditNote={handleEditNote}
                handleAddNewPhotos={(files, textNote, isEarly) =>
                  handleAddNotePhotos({ id: item?.id, files, textNote, isEarly })
                }
                key={index}
                item={item}
                handleDeleteNoteLead={handleDeleteNoteLead}
                handleAddTowYards={handleAddTowYards}
                handleDeleteTowYards={handleDeleteTowYards}
                handleChangeVinStatus={handleChangeVinStatus}
                handleUploadFileCarfax={handleUploadFileCarfax}
                handleDeleteDocumentLead={handleDeleteDocumentLead}
              />
            ))}
            <Stack gap={'10px'}>
              <WrapperButton>
                <ButtonCustom
                  sx={{ width: { md: '300px', xs: '100%' } }}
                  onClick={() => {
                    setModalNewLead(true);
                  }}
                  label={'New Lead'}
                />
              </WrapperButton>
              <WrapperButton>
                <ButtonCustom
                  sx={{
                    width: { md: '300px', xs: '100%' },
                    ...(file?.MVSRequested
                      ? { background: '#888', ':hover': { background: '#666' } }
                      : {})
                  }}
                  onClick={() => {
                    const isCancelling = file?.MVSRequested;
                    const message = isCancelling
                      ? 'Are you sure you want to cancel the MVS request?'
                      : 'Are you sure you want to request MVS?';
                    if (!window.confirm(message)) return;
                    asyncHandleRequestMvs(file?.id, !isCancelling);
                  }}
                  label={file?.MVSRequested ? 'Cancel MVS Request' : 'Request MVS'}
                />
              </WrapperButton>
            </Stack>
          </ListLead>
        </WrapperLead>
        <PhotoStack>
          <BoxPhotoMemo photos={file?.photos} />
        </PhotoStack>
        <BoxFileDocumentMemo documents={file?.fileDocuments} />
        <Stack px={2.5} mt={2}>
          <SummaryLog leads={leads} />
        </Stack>
        <Stack px={2.5} mt={2}>
          <ListBoxMemo fileId={Number(file?.id)} />
        </Stack>
        <Stack style={{ padding: '12px 0px', borderBottom: '1px solid #d2d2cf' }} gap={2}>
          <TitleLead style={{ padding: '0 11px' }}>Reminder</TitleLead>
          <Stack
            style={{
              margin: '0 11px',
              background: '#E3eef9',
              borderRadius: '10px',
              padding: '20px',
              minHeight: '100px',
              maxHeight: '300px',
              overflowY: 'scroll'
            }}
            flexDirection={'column'}
            gap={1}>
            {file?.reminders?.map((item, index) => {
              return (
                <Stack key={index} flexDirection={'column'} gap={1} width={'100%'}>
                  <Stack flexDirection={'row'} gap={1} flex={1}>
                    <Typography whiteSpace="pre-line" fontWeight="600">
                      {item.createdBy.role === ROLE.USER
                        ? `${item.createdBy.firstName} ${item.createdBy.lastName}`
                        : 'Admin'}
                    </Typography>
                    <Typography whiteSpace="pre-line" color={'#888'}>
                      - {dayjs(item.date).format('YYYY/MM/DD hh:mm A')}
                    </Typography>
                  </Stack>
                  <Typography marginLeft={1} whiteSpace="pre-line">
                    {item.note}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
          <Stack style={{ padding: '10px' }}>
            <ButtonCustom
              onClick={() => setOpenModalReminder(true)}
              style={{ height: '40px', width: '120px' }}
              label={'Reminder'}
            />
          </Stack>
          <FormReminder
            onSubmit={handleCreateReminder}
            isOpen={openModalReminder}
            handleClose={() => setOpenModalReminder(false)}
          />
        </Stack>
        <Stack
          px={2}
          gap={2}
          sx={{ flexDirection: { md: 'row', xs: 'column' } }}
          width={'100%'}
          alignItems={'center'}
          justifyContent={'center'}>
          <ButtonCustom
            sx={{ width: { md: '300px', xs: '100%' } }}
            label={'SEIZED'}
            onClick={onClickSeized}
            fullWidth
          />
          <ButtonCustom
            sx={{
              width: {
                md: '300px',
                xs: '100%',
                background: '#888',
                ':hover': {
                  background: '#888'
                }
              }
            }}
            label={'Condition Report'}
            onClick={() => setShowModalReport(true)}
            fullWidth
          />
          {file?.id && (
            <ModalReport
              fileId={Number(file.id)}
              onSubmitSuccess={asyncHandleCreateReport}
              isOpen={showModalReport}
              onClose={() => setShowModalReport(false)}
            />
          )}
          <ButtonCustom
            sx={{ width: { md: '300px', xs: '100%' } }}
            label={'Redemption'}
            isCancel
            onClick={handleUpdateBailiff}
            fullWidth
          />
        </Stack>
        <ModalNewLead
          isOpen={modalNewLead}
          onClose={() => setModalNewLead(false)}
          onSubmit={handleCreateLead}
          textTitle={'Add New Lead'}
        />
      </Stack>
    </Stack>
  );
}
export default FileDetailMobile;

const TitleLead = styled(Typography)(({ theme }: StyledParams) => ({
  color: theme.colors.light1,
  fontSize: 18,
  fontWeight: 500,
  lineHeight: '100%'
}));
const PhotoStack = styled(Typography)(({ theme }: StyledParams) => ({
  padding: '20px 0',
  margin: '0 16px',
  borderBottom: `1px solid ${theme.colors.light3}`
}));
const WrapperLead = styled(Stack)(() => ({
  padding: '20px 5px 0 5px'
}));
const WrapperButton = styled(Stack)(({ theme }: StyledParams) => ({
  borderBottom: `1px solid ${theme.colors.light3}`,
  width: '100%',
  alignItems: 'center'
}));
const ListLead = styled(Stack)(() => ({
  width: '100%',
  gap: 10,
  paddingTop: '20px'
}));
